import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { deleteTemplate, fetchAllTemplates } from 'store/actions/templates';
import request from 'utils/request';
import { setSnackbarSuccess } from 'store/actions/global';
import { templatesSelectors } from 'store/selectors/templates';

import { useConfirm } from 'material-ui-confirm';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import Grid from 'material/components/Grid';
import { Add as AddIcon } from '@mui/icons-material';

import { colors } from 'themes';

import { Backdrop, BreadcrumbsNavigation, Container, LinkText } from 'common/components';
import TopCard from 'common/containers/TopCard';
import { CardWrapper, CircularProgressWrapper } from 'globalStyles';

import TemplateItem from '../components/TemplateItem';

const Templates = () => {
  const dispatch = useDispatch();

  const { organizationUuid, type } = useParams();

  const { isFetching } = useSelector(state => ({
    isFetching: templatesSelectors.isFetching(state),
  }));

  const breadcrumbs = [
    {
      link:
        type === 'broadcast' ? `/${organizationUuid}/broadcasts` : `/${organizationUuid}/campaigns`,
      name: type === 'broadcast' ? 'Broadcasts' : 'Advocacy',
    },
    {
      link: '',
      name: 'Email Templates',
    },
  ];

  const [customTemplates, setCustomTemplates] = useState([]);

  const [previewTemplate, setPreviewTemplate] = useState(null);

  const handlePreviewTemplate = useCallback((templateUuid, templateName) => {
    setPreviewTemplate({
      message: null,
      name: templateName,
    });
    request.get(`/v1/broadcasts/templates/${templateUuid}/`).then(({ data }) =>
      setPreviewTemplate({
        message: data.message,
        name: data.name,
      })
    );
  }, []);

  const handlePreviewClose = () => setPreviewTemplate(null);

  useEffect(() => {
    dispatch(
      fetchAllTemplates({
        organization: organizationUuid,
        type,
      })
    ).then(data => setCustomTemplates(data || []));
  }, [type]);

  const confirm = useConfirm();

  const handleRemoveTemplate = templateUuid => {
    confirm({
      confirmationText: 'Remove',
      title: 'Remove Template?',
    })
      .then(() => dispatch(deleteTemplate(templateUuid)))
      .then(() =>
        dispatch(
          fetchAllTemplates({
            organization: organizationUuid,
            type,
          })
        )
      )
      .then(data => setCustomTemplates(data || []))
      .then(() => dispatch(setSnackbarSuccess()));
  };

  const templatesHeadline = useMemo(
    () => (type === 'broadcast' ? 'Broadcast Templates' : 'Action Alert Templates'),
    [type]
  );
  const templateType = useMemo(() => (type === 'broadcast' ? 'broadcast' : 'action alert'), [type]);

  const headerCellSx = {
    color: colors.osloGray,
    fontSize: '14px',
    fontWeight: 500,
  };

  return (
    <>
      <BreadcrumbsNavigation links={breadcrumbs} />
      <Container>
        <Grid container>
          <TopCard
            title={templatesHeadline}
            actionButtons={[
              {
                color: 'secondary',
                component: RouterLink,
                disableElevation: true,
                name: 'New Template',
                startIcon: <AddIcon />,
                to: `/${organizationUuid}/create-template/${type}`,
                variant: 'contained',
              },
            ]}
          />
        </Grid>
        <CardWrapper>
          {isFetching ? (
            <CircularProgressWrapper>
              <CircularProgress />
            </CircularProgressWrapper>
          ) : (
            <>
              {!customTemplates?.length && (
                <LinkText color="inherit" to={`/${organizationUuid}/create-template/${type}`}>
                  <span>{`Create your first ${templateType} email template.`}</span>
                </LinkText>
              )}
              {!!customTemplates?.length && (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx}>Name</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customTemplates.map((template, index) => (
                      <TemplateItem
                        handlePreviewTemplate={uuid => handlePreviewTemplate(uuid, template.name)}
                        handleRemoveTemplate={handleRemoveTemplate}
                        index={index}
                        name={template.name}
                        uuid={template.uuid}
                        key={template.uuid}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </CardWrapper>
      </Container>
      <Backdrop isOpen={isFetching} />
      <Dialog
        aria-labelledby="template-preview-dialog-title"
        maxWidth="lg"
        open={Boolean(previewTemplate)}
        onClose={handlePreviewClose}
      >
        <DialogTitle id="template-preview-dialog-title">{previewTemplate?.name}</DialogTitle>
        <DialogContent>
          {previewTemplate?.message === null ? (
            <CircularProgressWrapper>
              <CircularProgress />
            </CircularProgressWrapper>
          ) : (
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: previewTemplate?.message }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePreviewClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Templates;
